import { useState, useEffect, useRef } from "react";
import { readProgress, estimateLevel } from "../utils/progress.js";
import { vocabGroups } from "../content/vocabGroups.js";
import { getLanguageContent } from "../content/index.js";

type Tense = "present" | "preterite" | "imperfect" | "future" | "conditional" | "present_subjunctive";

type GeneratedSentence = {
  spanish?: string;
  portuguese?: string;
  english: string;
  timestamp: number;
  userAttempt?: string;
  isCorrect?: boolean;
  explanation?: string;
  suggestions?: string[];
};

type SavedSentence = GeneratedSentence & {
  id: string;
};

const SPANISH_TENSES = [
  "present",
  "preterite", 
  "imperfect",
  "future",
  "conditional",
  "present_subjunctive",
] as const;

const PORTUGUESE_TENSES = [
  "present",
  "preterite", 
  "imperfect",
  "future",
  "conditional",
  "present_subjunctive",
] as const;

function getKnownVocabulary(): string[] {
  const progress = readProgress();
  return Object.entries(progress.vocabularyByTerm)
    .map(([term, stats]) => ({ term, accuracy: stats.attempts > 0 ? stats.correct / stats.attempts : 0 }))
    .filter(({ accuracy }) => accuracy >= 0.6)
    .map(({ term }) => term);
}

function getKnownConjunctions(): string[] {
  const connectorsGroup = vocabGroups.find(g => g.id === "connectors");
  const conjunctionsGroup = vocabGroups.find(g => g.id === "conjunctions");
  
  const allConnectors = [
    ...(connectorsGroup?.tiers.A1 || []),
    ...(connectorsGroup?.tiers.A2 || []),
    ...(conjunctionsGroup?.tiers.A1 || []),
    ...(conjunctionsGroup?.tiers.A2 || []),
  ];
  
  return allConnectors.map(w => w.term);
}

function getRandomVocabularySubset(knownVocab: string[], count: number = 5): string[] {
  const shuffled = [...knownVocab].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, knownVocab.length));
}

function getSavedSentences(): SavedSentence[] {
  if (typeof window === "undefined") return [];
  try {
    const saved = localStorage.getItem("sentencePractice");
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function saveSentence(sentence: GeneratedSentence): void {
  if (typeof window === "undefined") return;
  try {
    const saved = getSavedSentences();
    const newSentence: SavedSentence = {
      ...sentence,
      id: Date.now().toString(),
    };
    saved.unshift(newSentence);
    // Keep only last 50 sentences
    const limited = saved.slice(0, 50);
    localStorage.setItem("sentencePractice", JSON.stringify(limited));
  } catch (error) {
    console.error("Failed to save sentence:", error);
  }
}

async function generateSentenceWithChatGPT(
  selectedTenses: Tense[],
  knownVocab: string[],
  conjunctions: string[],
  userLevel: string,
  language: string
): Promise<GeneratedSentence | null> {
  try {
    const vocabSubset = getRandomVocabularySubset(knownVocab, 8);
    const targetLanguage = language === "es" ? "Spanish" : "Portuguese";
    
    const prompt = `Generate a SHORT ${targetLanguage} practice sentence for a language learner. Use these requirements:

USER LEVEL: ${userLevel}

GRAMMATICAL FORMS TO INCLUDE: ${selectedTenses.join(", ")}

KNOWN VOCABULARY (use 2-3 words from this list): ${vocabSubset.join(", ")}

CONJUNCTIONS/CONNECTORS (use 1 from this list if appropriate): ${conjunctions.join(", ")}

Requirements:
- Create a SHORT, MEANINGFUL sentence (maximum 6-8 words) that makes sense
- Use one of the specified grammatical forms
- Include 2-3 words from the vocabulary list in a logical way
- Optionally use 1 conjunction/connector if it fits naturally
- The sentence must be coherent and realistic
- For A1: Simple, everyday actions
- For A2: Past actions and basic descriptions
- For B1+: Conditional logic and complex structures

Respond in this exact JSON format:
{
  "${language === "es" ? "spanish" : "portuguese"}": "Your short ${targetLanguage} sentence here",
  "english": "Your English translation here"
}`;

    const response = await fetch("/api/generate-sentence", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ prompt }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    if ((data.spanish || data.portuguese) && data.english) {
      return {
        spanish: data.spanish,
        portuguese: data.portuguese,
        english: data.english,
        timestamp: Date.now(),
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

async function checkAnswerWithChatGPT(
  correctAnswer: string,
  userAnswer: string,
  englishTranslation: string,
  userLevel: string,
  language: string
): Promise<{ isCorrect: boolean; explanation: string; suggestions: string[] } | null> {
  try {
    const response = await fetch("/api/check-answer", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        correctAnswer,
        userAnswer,
        englishTranslation,
        userLevel,
        language,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.isCorrect !== undefined && data.explanation) {
      return {
        isCorrect: data.isCorrect,
        explanation: data.explanation,
        suggestions: data.suggestions || [],
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

export function SentencePractice({ showControls = false, language = "es" }: { showControls?: boolean; language?: string }) {
  const [selectedTenses, setSelectedTenses] = useState<Tense[]>(["present", "preterite", "imperfect"]);
  const [currentSentence, setCurrentSentence] = useState<GeneratedSentence | null>(null);
  const [userInput, setUserInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [savedSentences, setSavedSentences] = useState<SavedSentence[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [userLevel, setUserLevel] = useState<{ label: string; progressToNext: number; score: number }>({ label: "A1", progressToNext: 0, score: 0 });
  const [sentenceLevel, setSentenceLevel] = useState<"A1" | "A2" | "B1" | "B2" | "C1" | "C2">("A1");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSavedSentences(getSavedSentences());
    const est = estimateLevel();
    setUserLevel(est);
    try {
      const stored = localStorage.getItem("sentenceLevel");
      if (stored === "A1" || stored === "A2" || stored === "B1" || stored === "B2" || stored === "C1" || stored === "C2") {
        setSentenceLevel(stored);
      } else {
        setSentenceLevel(est.label as any);
      }
    } catch {
      setSentenceLevel(est.label as any);
    }
    
    // Auto-generate a sentence when the component loads
    handleGenerateSentence();
  }, []);

  const handleGenerateSentence = async () => {
    setIsGenerating(true);
    setShowAnswer(false);
    setUserInput("");
    
    const knownVocab = getKnownVocabulary();
    const conjunctions = getKnownConjunctions();
    
    const sentence = await generateSentenceWithChatGPT(selectedTenses, knownVocab, conjunctions, sentenceLevel, language);
    
    if (sentence) {
      setCurrentSentence(sentence);
      inputRef.current?.focus();
    }
    
    setIsGenerating(false);
  };

  const handleCheckAnswer = async () => {
    if (!currentSentence || !userInput.trim()) return;
    
    setIsChecking(true);
    
    try {
      const correctAnswer = language === "es" ? currentSentence.spanish : currentSentence.portuguese;
      const feedback = await checkAnswerWithChatGPT(
        correctAnswer || "",
        userInput.trim(),
        currentSentence.english,
        sentenceLevel,
        language
      );
      
      if (feedback) {
        const updatedSentence = {
          ...currentSentence,
          userAttempt: userInput,
          isCorrect: feedback.isCorrect,
          explanation: feedback.explanation,
          suggestions: feedback.suggestions,
        };
        
        setCurrentSentence(updatedSentence);
        setShowAnswer(true);
        
        // Save the sentence for later practice
        saveSentence(updatedSentence);
        setSavedSentences(getSavedSentences());
      } else {
        // Fallback to simple check if ChatGPT fails
        const normalizedInput = userInput.trim().toLowerCase();
        const correctAnswer = language === "es" ? currentSentence.spanish : currentSentence.portuguese;
        const normalizedAnswer = correctAnswer?.toLowerCase() || "";
        const isCorrect = normalizedInput === normalizedAnswer;
        
        const updatedSentence = {
          ...currentSentence,
          userAttempt: userInput,
          isCorrect,
          explanation: isCorrect 
            ? "¡Correcto! Your answer matches the expected sentence perfectly." 
            : "Not quite right. Compare your answer with the correct sentence above. Look for differences in verb conjugation, articles, word order, or spelling.",
        };
        
        setCurrentSentence(updatedSentence);
        setShowAnswer(true);
        
        saveSentence(updatedSentence);
        setSavedSentences(getSavedSentences());
      }
    } catch (error) {
      // Fallback to simple check
      const normalizedInput = userInput.trim().toLowerCase();
      const correctAnswer = language === "es" ? currentSentence.spanish : currentSentence.portuguese;
      const normalizedAnswer = correctAnswer?.toLowerCase() || "";
      const isCorrect = normalizedInput === normalizedAnswer;
      
      const updatedSentence = {
        ...currentSentence,
        userAttempt: userInput,
        isCorrect,
        explanation: isCorrect 
          ? "¡Correcto! Your answer matches the expected sentence perfectly." 
          : "Not quite right. Compare your answer with the correct sentence above. Look for differences in verb conjugation, articles (el/la), word order, or spelling.",
      };
      
      setCurrentSentence(updatedSentence);
      setShowAnswer(true);
      
      saveSentence(updatedSentence);
      setSavedSentences(getSavedSentences());
    } finally {
      setIsChecking(false);
    }
  };

  const handleNext = () => {
    setUserInput("");
    setShowAnswer(false);
    // Auto-generate next sentence
    handleGenerateSentence();
  };

  const handleToggleTense = (tense: Tense) => {
    setSelectedTenses(prev => 
      prev.includes(tense) 
        ? prev.filter(t => t !== tense)
        : [...prev, tense]
    );
  };

  const handleRetrySaved = (sentence: SavedSentence) => {
    setCurrentSentence({
      spanish: sentence.spanish,
      portuguese: sentence.portuguese,
      english: sentence.english,
      timestamp: Date.now(),
    });
    setUserInput("");
    setShowAnswer(false);
    setShowSaved(false);
    inputRef.current?.focus();
  };

  const availableTenses = language === "es" ? SPANISH_TENSES : PORTUGUESE_TENSES;

  return (
    <div className="space-y-4">
      {/* Main Exercise Area - Central Focus with Fixed Dimensions */}
      <div className="text-center mt-16 sm:mt-20">
        <div className="p-4 sm:p-6 rounded-xl bg-white/80 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 min-h-[250px] sm:min-h-[300px] flex flex-col justify-center">
          {isGenerating ? (
            /* Loading State */
            <div className="flex flex-col items-center space-y-4">
              {/* Language-themed loading animation */}
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 rounded-full animate-spin">
                  <div className="absolute top-0 left-0 w-4 h-4 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-2xl animate-bounce">📝</div>
                </div>
              </div>
              
              {/* Typewriter effect simulation */}
              <div className="flex items-center justify-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                <span>AI is writing</span>
                <div className="flex space-x-0.5">
                  <div className="w-1 h-4 bg-blue-500 animate-pulse"></div>
                  <div className="w-1 h-4 bg-blue-500 animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <div className="w-1 h-4 bg-blue-500 animate-pulse" style={{animationDelay: '0.4s'}}></div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Crafting your practice sentence...
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Selecting vocabulary and grammar forms tailored to your level
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 animate-pulse">
                  ✨ Personalizing your learning experience
                </div>
              </div>
            </div>
          ) : currentSentence ? (
            /* Active Exercise */
            <div className="w-full">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                Practice Sentence
              </div>
              
              <div className="text-gray-800 dark:text-gray-200 mb-6 text-xl font-medium min-h-[60px] flex items-center justify-center">
                {currentSentence.english}
              </div>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <input
                  ref={inputRef}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !showAnswer) {
                      handleCheckAnswer();
                    } else if (e.key === "Enter" && showAnswer) {
                      handleNext();
                    }
                  }}
                  className="flex-1 px-4 py-3 rounded-xl border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 text-base sm:text-lg min-h-[44px]"
                  placeholder={`Type your ${language === "es" ? "Spanish" : "Portuguese"} translation here...`}
                  disabled={showAnswer}
                />
                
                {!showAnswer ? (
                  <button
                    onClick={handleCheckAnswer}
                    disabled={isChecking || !userInput.trim()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-600 bg-blue-600 px-6 py-3 text-white text-base font-medium shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {isChecking ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="hidden sm:inline">Analyzing...</span>
                        <span className="sm:hidden">Check</span>
                      </>
                    ) : (
                      <>
                        Check
                        <span className="hidden sm:inline text-xs opacity-75">(Enter)</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-green-600 bg-green-600 px-6 py-3 text-white text-base font-medium shadow-sm hover:bg-green-500 min-h-[44px]"
                  >
                    Next
                    <span className="hidden sm:inline text-xs opacity-75">(Enter)</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* Error State */
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Unable to generate sentence
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                Please make sure you have selected at least one grammatical form below.
              </div>
              <button
                onClick={handleGenerateSentence}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-600 bg-blue-600 px-4 py-2 text-white text-sm font-medium shadow-sm hover:bg-blue-500"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Section - Below Exercise */}
      {currentSentence && showAnswer && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-white/60 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
            <div className="text-sm">
              <span className="text-gray-600 dark:text-gray-400">Correct answer: </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {language === "es" ? currentSentence.spanish : currentSentence.portuguese}
              </span>
            </div>
            
            {currentSentence.userAttempt && (
              <div className={`text-sm mt-2 ${
                currentSentence.isCorrect 
                  ? "text-green-700 dark:text-green-400" 
                  : "text-red-700 dark:text-red-400"
              }`}>
                Your answer: "{currentSentence.userAttempt}" 
                {currentSentence.isCorrect ? " ✓ Correct!" : " ✗ Try again"}
              </div>
            )}
          </div>

          {currentSentence.explanation && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {currentSentence.isCorrect ? "✅ Explanation:" : "📝 Feedback:"}
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {currentSentence.explanation}
              </div>
            </div>
          )}

          {currentSentence.suggestions && currentSentence.suggestions.length > 0 && (
            <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                💡 Suggestions:
              </div>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                {currentSentence.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Collapsible controls */}
      {showControls && (
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Controls</h3>
          
          {/* User Level Display */}
          <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
              Your Inferred Level: {userLevel.label}
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-300">
              System estimate from your activity
              {userLevel.progressToNext > 0 && (
                <span className="ml-2">
                  ({userLevel.progressToNext}% to next)
                </span>
              )}
            </div>
          </div>

          {/* Target Sentence Level Selection */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Target sentence level</label>
            <div className="flex items-center gap-2">
              <select
                className="text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                value={sentenceLevel}
                onChange={(e) => {
                  const v = e.target.value as typeof sentenceLevel;
                  setSentenceLevel(v);
                  try { localStorage.setItem("sentenceLevel", v); } catch {}
                }}
                aria-label="Target sentence level"
              >
                {(["A1","A2","B1","B2","C1","C2"] as const).map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <button
                onClick={handleGenerateSentence}
                disabled={isGenerating}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-3 py-2 text-white text-sm shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
              >
                New with this level
              </button>
            </div>
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Choose the difficulty of the next sentence.
            </div>
          </div>

          {/* Tense Selection */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Select grammatical forms to practice</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {availableTenses.map((tense) => {
                const isSelected = selectedTenses.includes(tense);
                return (
                  <label key={tense} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={isSelected}
                      onChange={() => handleToggleTense(tense)}
                    />
                    <span className="text-gray-800 dark:text-gray-200 capitalize">
                      {tense.replaceAll("_", " ")}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleGenerateSentence}
              disabled={isGenerating || selectedTenses.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-3 py-2 text-white text-sm shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isGenerating ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Crafting...</span>
                  <span className="sm:hidden">Generate</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Generate New Sentence</span>
                  <span className="sm:hidden">Generate</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => setShowSaved(!showSaved)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800 min-h-[44px]"
            >
              {showSaved ? "Hide" : "Show"} Saved ({savedSentences.length})
            </button>
          </div>
        </div>
      )}

      {/* Saved Sentences Section */}
      {showSaved && (
        <div className="p-4 rounded-xl bg-white/60 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Previously Practiced Sentences
          </div>
          {savedSentences.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400 text-sm">No saved sentences yet.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {savedSentences.map((sentence) => (
                <div key={sentence.id} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <div className="flex-1">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {language === "es" ? sentence.spanish : sentence.portuguese}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {sentence.english}
                    </div>
                    {sentence.userAttempt && (
                      <div className={`text-xs mt-1 ${
                        sentence.isCorrect ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}>
                        Your attempt: "{sentence.userAttempt}" {sentence.isCorrect ? "✓" : "✗"}
                      </div>
                    )}
                    {sentence.explanation && (
                      <div className="text-xs mt-1 text-gray-600 dark:text-gray-400 line-clamp-2">
                        {sentence.explanation}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRetrySaved(sentence)}
                    className="ml-2 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Retry
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

